import shaka from 'shaka-player';
import { fetchAdScheduleXml } from './adScheduleProxy';
import type { AdsInitContext } from './initContext';
import { loadImaSdk } from './loadImaSdk';

type ShakaAdManager = ReturnType<shaka.Player['getAdManager']>;

type ShakaAdObject = {
  getRemainingTime?: () => number;
  getDuration?: () => number;
  getPositionInSequence?: () => number;
  getSequenceLength?: () => number;
  canSkipNow?: () => boolean;
  isSkippable?: () => boolean;
  getTimeUntilSkippable?: () => number;
  skip?: () => void;
};

export type AdTimePayload = {
  position: number;
  duration: number;
  remaining: number;
  sequencePosition: number;
  sequenceLength: number;
  /** IMA reports the ad can be skipped right now. */
  canSkip: boolean;
  /** VAST declares a skip offset (may count down before `canSkip`). */
  isSkippable: boolean;
  skipInSeconds: number;
};

/**
 * Client-side ads (Google IMA) via Shaka Player's AdManager.
 */
export type CsaiAdapter = {
  init: (ctx: AdsInitContext) => Promise<void>;
  requestAds: () => Promise<void>;
  destroy: () => void;
};

function readAdFromEvent(event: Event): ShakaAdObject | null {
  const shakaEvent = event as Event & { detail?: Map<string, unknown> } & Record<string, unknown>;
  const fromKey = shakaEvent.ad ?? shakaEvent['ad'];
  if (fromKey) return fromKey as ShakaAdObject;
  if (shakaEvent.detail instanceof Map) {
    const fromDetail = shakaEvent.detail.get('ad');
    if (fromDetail) return fromDetail as ShakaAdObject;
  }
  return null;
}

function resolveSkipState(ad: ShakaAdObject): Pick<AdTimePayload, 'canSkip' | 'isSkippable' | 'skipInSeconds'> {
  const isSkippable = ad.isSkippable?.() ?? false;
  const skipInSeconds = Math.max(0, Math.ceil(ad.getTimeUntilSkippable?.() ?? 0));
  // IMA can report canSkipNow early; require countdown elapsed before showing/acting on skip.
  const canSkip = isSkippable && skipInSeconds <= 0 && ad.canSkipNow?.() === true;
  return { canSkip, isSkippable, skipInSeconds };
}

function readAdProgress(ad: ShakaAdObject | null): AdTimePayload | null {
  if (!ad) return null;

  const remaining = typeof ad.getRemainingTime === 'function' ? ad.getRemainingTime() : Number.NaN;
  const duration = typeof ad.getDuration === 'function' ? ad.getDuration() : Number.NaN;
  if (!Number.isFinite(remaining) || !Number.isFinite(duration) || duration <= 0) return null;

  const position = Math.max(0, duration - remaining);
  const sequencePosition = ad.getPositionInSequence?.() ?? 1;
  const sequenceLength = ad.getSequenceLength?.() ?? 1;

  return {
    position,
    duration,
    remaining: Math.max(0, remaining),
    sequencePosition,
    sequenceLength,
    ...resolveSkipState(ad),
  };
}

export function createCsaiAdapter(): CsaiAdapter {
  let adManager: ShakaAdManager | null = null;
  let adConfig: AdsInitContext['adConfig'];
  let video: HTMLVideoElement | null = null;
  let bus: AdsInitContext['bus'] | null = null;
  let currentAd: ShakaAdObject | null = null;
  let imaAdsManager: google.ima.AdsManager | null = null;
  let skipRequestHandler: (() => void) | null = null;
  const listeners: Array<{ event: string; handler: EventListener }> = [];

  const emitAdTime = (ad: ShakaAdObject | null) => {
    const progress = readAdProgress(ad);
    if (progress) bus?.emit('adTime', progress);
  };

  const addListener = (eventName: string, handler: EventListener) => {
    if (!adManager) return;
    adManager.addEventListener(eventName, handler);
    listeners.push({ event: eventName, handler });
  };

  const removeListeners = () => {
    if (!adManager) return;
    listeners.forEach(({ event, handler }) => {
      adManager?.removeEventListener(event, handler);
    });
    listeners.length = 0;
  };

  return {
    async init(ctx) {
      adConfig = ctx.adConfig;
      video = ctx.video;
      bus = ctx.bus;

      if (!adConfig?.schedule) return;

      skipRequestHandler = () => {
        if (!currentAd) return;
        const { canSkip } = resolveSkipState(currentAd);
        if (!canSkip) return;
        // IMA AdsManager.skip() is a no-op when IMA renders its own skip button.
        if (imaAdsManager) {
          imaAdsManager.discardAdBreak();
          return;
        }
        currentAd.skip?.();
      };
      bus.on('adSkipRequest', skipRequestHandler);

      await loadImaSdk();
      if (!globalThis.google?.ima?.AdsRequest) {
        console.warn('[CSAI] Google IMA SDK unavailable after load');
        return;
      }

      adManager = ctx.shakaPlayer.getAdManager();
      if (!adManager) {
        console.warn('[CSAI] Shaka AdManager unavailable');
        return;
      }

      const controls = ctx.uiOverlay.getControls?.();
      const csContainer = controls?.getClientSideAdContainer?.();
      if (!csContainer) {
        console.warn('[CSAI] Shaka client-side ad container not found');
        return;
      }

      const adsRenderingSettings = new google.ima.AdsRenderingSettings();
      adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      adManager.initClientSide(csContainer, ctx.video, adsRenderingSettings);

      addListener(shaka.ads.Utils.IMA_AD_MANAGER_LOADED, (event) => {
        const imaEvent = event as Event & { imaAdManager?: google.ima.AdsManager };
        imaAdsManager = imaEvent.imaAdManager ?? null;
      });

      addListener(shaka.ads.Utils.AD_STARTED, (event) => {
        currentAd = readAdFromEvent(event);
        bus?.emit('adBreakStart');
        emitAdTime(currentAd);
      });

      addListener(shaka.ads.Utils.AD_STOPPED, () => {
        currentAd = null;
        bus?.emit('adBreakEnd');
      });

      addListener(shaka.ads.Utils.AD_COMPLETE, () => {
        currentAd = null;
        bus?.emit('adBreakEnd');
      });

      addListener(shaka.ads.Utils.AD_IMPRESSION, () => {
        bus?.emit('adImpression');
      });

      addListener(shaka.ads.Utils.AD_PROGRESS, (event) => {
        currentAd = readAdFromEvent(event) ?? currentAd;
        emitAdTime(currentAd);
      });

      addListener(shaka.ads.Utils.AD_SKIP_STATE_CHANGED, (event) => {
        currentAd = readAdFromEvent(event) ?? currentAd;
        emitAdTime(currentAd);
      });
    },

    async requestAds() {
      if (!adManager || !adConfig?.schedule || !video) return;
      if (!globalThis.google?.ima?.AdsRequest) return;

      const rect = video.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width || video.clientWidth || 640));
      const height = Math.max(1, Math.round(rect.height || video.clientHeight || 360));

      const adsRequest = new google.ima.AdsRequest();
      adsRequest.linearAdSlotWidth = width;
      adsRequest.linearAdSlotHeight = height;
      adsRequest.nonLinearAdSlotWidth = width;
      adsRequest.nonLinearAdSlotHeight = height;

      try {
        adsRequest.adsResponse = await fetchAdScheduleXml(adConfig.schedule);
      } catch (e) {
        console.error('[CSAI] Failed to load ad schedule XML', e);
        return;
      }

      try {
        adManager.requestClientSideAds(adsRequest);
      } catch (e) {
        console.error('[CSAI] requestClientSideAds failed', e);
      }
    },

    destroy() {
      if (bus && skipRequestHandler) {
        bus.off('adSkipRequest', skipRequestHandler);
      }
      skipRequestHandler = null;
      removeListeners();
      currentAd = null;
      imaAdsManager = null;
      adManager = null;
      adConfig = undefined;
      video = null;
      bus = null;
    },
  };
}
